import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: current } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [draft, setDraft] = useState({ name: "", email: "", password: "", role: "sales_user" });

  const { data, isLoading } = useQuery({
    queryKey: ["users", page, q],
    queryFn: () => get("/users", { page, page_size: 25, q: q || undefined }),
  });
  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: () => get("/roles") });

  const create = useMutation({
    mutationFn: () => api.post("/users", draft),
    onSuccess: () => {
      toast.success("User created");
      setOpen(false);
      setDraft({ name: "", email: "", password: "", role: "sales_user" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const update = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/users/${id}`, payload),
    onSuccess: () => {
      toast.success("User updated");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => {
      toast.success("User removed");
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      <PageHeader
        testId="users-page"
        title="Users & roles"
        subtitle="Backend-enforced role permissions for every operator"
        actions={
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)} data-testid="new-user-button">
            New user
          </Button>
        }
      />

      <Section
        testId="users-section"
        title={`${data?.total ?? 0} users`}
        actions={<Input data-testid="users-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search users" className="h-9 w-48" />}
      >
        <DataTable
          testId="users-table"
          rowTestId="user-row"
          loading={isLoading}
          columns={[
            { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
            { key: "email", label: "Email" },
            { key: "role_label", label: "Role", render: (r) => <StatusBadge value={r.role === "super_admin" ? "hot" : "new"} label={r.role_label} /> },
            { key: "is_active", label: "Active", render: (r) => (
              <Switch
                data-testid={`user-toggle-${r.id}`}
                checked={r.is_active}
                disabled={r.id === current?.id}
                onCheckedChange={(checked) => update.mutate({ id: r.id, payload: { is_active: checked } })}
              />
            ) },
            { key: "last_login_at", label: "Last login", render: (r) => dt(r.last_login_at) },
            { key: "created_at", label: "Created", render: (r) => dt(r.created_at) },
            {
              key: "role_change",
              label: "Change role",
              render: (r) => (
                <Select value={r.role} onValueChange={(v) => update.mutate({ id: r.id, payload: { role: v } })}>
                  <SelectTrigger data-testid={`user-role-${r.id}`} className="h-8 w-44"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(roles || []).map((role) => <SelectItem key={role.key} value={role.key}>{role.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ),
            },
            {
              key: "actions",
              label: "",
              render: (r) =>
                r.id === current?.id ? null : (
                  <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => setRemoving(r)} data-testid={`user-delete-${r.id}`}>
                    Remove
                  </Button>
                ),
            },
          ]}
          rows={data?.items || []}
          empty={{ title: "No users found" }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Section title="Role permissions" testId="roles-matrix">
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
          {(roles || []).map((role) => (
            <div key={role.key} className="rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-semibold text-slate-800">{role.label}</p>
              <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                {Object.entries(role.permissions).map(([resource, actions]) => (
                  <li key={resource}>
                    {title(resource)}: <span className="font-mono">{actions.join(", ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="user-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="user-email-input" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <Input data-testid="user-password-input" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(roles || []).map((role) => <SelectItem key={role.key} value={role.key}>{role.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!draft.name || !draft.email || draft.password.length < 6 || create.isPending}
              onClick={() => create.mutate()}
              data-testid="user-create-submit"
            >
              Create user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        destructive
        title={`Remove ${removing?.name}?`}
        description="The account is soft-deleted and can no longer sign in. The action is audited."
        confirmLabel="Remove user"
        onConfirm={() => remove.mutateAsync(removing.id)}
        testId="user-remove-dialog"
      />
    </>
  );
}
